<template>
  <div style="max-width: 600px" class="q-mx-auto q-mt-md">
    <div class="text-right q-mb-sm">
      <q-btn
        label="Save"
        color="primary"
        :loading="saving"
        @click="handleSubmit"
      />
    </div>

    <q-card flat bordered>
      <q-card-section>
        <app-editable-input
          :data="name"
          name="name"
          @input="
            name = $event.model;
            delayTouch($v.name, $options.touchMap);
          "
          text="Name *"
          :error="$v.name.$error"
        />
        <app-select-input
          clearable
          use-input
          v-model="emailTemplate"
          :options="emailTemplateOptions"
          option-label="name"
          option-value="id"
          input-debounce="0"
          @filter="filterEmailFn"
          text="Email Template"
          hint="If you would like to send a marketing email, choose email template above."
        />

        <app-select-input
          clearable
          use-input
          v-model="smsTemplate"
          :options="smsTemplateOptions"
          option-label="name"
          option-value="id"
          input-debounce="0"
          @filter="filterSMSFn"
          text="SMS Template"
          hint="If you would like to send a marketing text message,
          choose text messaging template above."
        />

        <app-select-input
          clearable
          use-input
          v-model="senderUser"
          :options="assignees"
          option-value="id"
          :option-label="(item) => `${item.firstName} ${item.lastName}`"
          input-debounce="0"
          text="Sender"
        />

        <app-select-input
          v-model="baseDate"
          :options="baseDateOptions"
          option-label="label"
          option-value="value"
          text="Base Date"
          @input="delayTouch($v.baseDate, $options.touchMap)"
          emit-value
          map-options
          :error="$v.baseDate.$error"
        />
        <app-select-input
          v-model="days"
          :options="daysOptions"
          text="Days"
          @input="delayTouch($v.days, $options.touchMap)"
          :error="$v.days.$error"
        />
        <app-select-input
          v-model="hours"
          :options="hoursOptions"
          text="Hours"
          @input="delayTouch($v.hours, $options.touchMap)"
          :error="$v.hours.$error"
        />
        <app-editable-input
          :data="description"
          name="description"
          @input="description = $event.model"
          text="Description"
        />
        <app-select-input
          v-model="requiredEvent"
          :options="requiredEventsOptions"
          text="Required Event?"
          emit-value
          map-options
        />
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
import {
  MARKETING_BASE_ON_RECEIVED_DATE,
  MARKETING_BASE_ON_AVAILABLE_DATE,
  MARKETING_BASE_ON_DISPATCH_DATE,
  BASE_DATE_OPTIONS
} from '@server/constants/marketing';
import { marketingValidator } from '@client/validators';
import graphql from '@client/graphql';
import AppEditableInput from '@client/components/form/editable-input';
import AppSelectInput from '@client/components/form/select-input';

const baseDateOptions = [
  {
    label: BASE_DATE_OPTIONS[MARKETING_BASE_ON_RECEIVED_DATE],
    value: MARKETING_BASE_ON_RECEIVED_DATE
  },
  {
    label: BASE_DATE_OPTIONS[MARKETING_BASE_ON_AVAILABLE_DATE],
    value: MARKETING_BASE_ON_AVAILABLE_DATE
  },
  {
    label: BASE_DATE_OPTIONS[MARKETING_BASE_ON_DISPATCH_DATE],
    value: MARKETING_BASE_ON_DISPATCH_DATE
  }
];

const daysOptions = [
  -60, -45, -30, -15, -14, -13, -12, -11, -10, -9, -8, -7, -6, -5, -4, -3, -2,
  -1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 30, 45, 60
];
const hoursOptions = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22
];

const requiredEventsOptions = [
  {
    label: 'Yes',
    value: true
  },
  {
    label: 'No',
    value: false
  }
];

export default {
  metaInfo: {
    title: 'AutoBook Pro - Marketings'
  },
  components: {
    AppEditableInput,
    AppSelectInput
  },
  validations: marketingValidator,
  apollo: {
    marketing: {
      query: graphql.queries.getMarketing,
      variables() {
        return {
          id: this.$route.params.id
        };
      },
      watchLoading(isLoading) {
        this.loading = isLoading;
      },
      error(error) {
        this.error = error;
      },
      result(result) {
        if (result.loading) return;

        const { marketing } = result.data;
        Object.assign(this, marketing);
      },
      skip() {
        return this.$route.name === 'create-marketing';
      }
    },
    emailTemplates: {
      query: graphql.queries.getEmailTemplates
    },
    smsTemplates: {
      query: graphql.queries.getSMSTemplates
    },
    assignees: {
      query: graphql.queries.loadAssignees
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      saving: false,

      name: null,
      baseDate: baseDateOptions[0].value,
      baseDateOptions,
      emailTemplate: null,
      smsTemplate: null,

      senderUser: null,

      days: 0,
      daysOptions,

      hours: 0,
      hoursOptions,

      description: '',

      requiredEvent: requiredEventsOptions[1].value,
      requiredEventsOptions,

      emailTemplateOptions: [],
      smsTemplateOptions: []
    };
  },
  computed: {
    marketingType() {
      let { type } = this.$route.query;
      if (type !== 'QUOTE' && type !== 'ORDER') type = 'QUOTE';
      return type;
    }
  },
  methods: {
    async handleSubmit() {
      this.validate(['name', 'baseDate', 'days', 'hours']);

      const payload = {};
      payload.name = this.name;
      if (this.emailTemplate) {
        payload.emailTemplateId = parseInt(this.emailTemplate.id, 10);
      }
      if (this.smsTemplate) {
        payload.smsTemplateId = parseInt(this.smsTemplate.id, 10);
      }
      if (this.senderUser) {
        payload.senderUserId = parseInt(this.senderUser.id, 10);
      }
      payload.baseDate = this.baseDate;
      payload.days = parseInt(this.days, 10);
      payload.hours = parseInt(this.hours, 10);
      payload.description = this.description;
      payload.requiredEvent = this.requiredEvent;

      this.saving = true;

      if (this.$route.name === 'create-marketing') {
        payload.type = this.marketingType;

        await this.$apollo.mutate({
          mutation: graphql.mutations.createMarketing,
          variables: { input: payload }
        });
      } else if (this.$route.name === 'marketing-detail') {
        await this.$apollo.mutate({
          mutation: graphql.mutations.updateMarketing,
          variables: { id: this.$route.params.id, input: payload }
        });
      }

      this.saving = false;

      this.$router.push({ name: 'marketings' });
    },
    filterEmailFn(val, update) {
      update(() => {
        const needle = val.toLowerCase();
        this.emailTemplateOptions = this.emailTemplates.filter(
          (v) => v.name.toLowerCase().indexOf(needle) > -1
        );
      });
    },
    filterSMSFn(val, update) {
      update(() => {
        const needle = val.toLowerCase();
        this.smsTemplateOptions = this.smsTemplates.filter(
          (v) => v.name.toLowerCase().indexOf(needle) > -1
        );
      });
    },
    filterPhoneNumbersFn(val, update) {
      update(() => {
        const needle = val.toLowerCase();
        this.phoneNumberOptions = this.accountPhoneNumbers.filter(
          (el) => el.number.toLowerCase().indexOf(needle) > -1
        ).map((el) => ({
          ...el,
          numberWithExt: `${el.number} Ext:${el.extension}`
        }));
      });
    }
  }
};
</script>

<style></style>
